import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Panel,
  Configure,
  connectRefinementList,
} from 'react-instantsearch-dom';
import moment from 'moment/moment';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { debounce, isArray, isEqual } from 'lodash';
import currency from 'currency.js';
import { SearchState } from 'react-instantsearch-core';
import Swal from 'sweetalert2';
import {
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_SORT_BY_LOCATION_IF_SET,
  algoliaPrecisionSettings,
  listingAttributesToHighlightPuppyCard,
  listingAttributesToRetrievePuppyCard,
  listingSearchRefinements,
  publiclySearchableAllListingsAttributes,
  validAgeSelectionOptions,
} from '../../../utils/constants';
import { ResultsNumberMobile, NoResults } from '../../search/widgets';
import getEmptyFilter from '../../search/widgets/filters/empty-filter';
import getSelectDropdown from '../../search/widgets/filters/select-dropdown';
import SelectWidget from '../../search/widgets/SelectWidget';
import useRequestLocation from '../../../hooks/useRequestLocation';
import CustomAgeFilter from '../../forms/Partials/CustomAgeFilter';
import CustomPriceFilter from '../../search/widgets/filters/CustomPriceFilter';
import ClearRefinements from '../../search/widgets/ClearRefinements';
import {
  AdminSingleBreedToggleStateInterface,
  AgeParams,
  FacetObject,
  PriceParams,
  QueryParams,
} from '../../search/admin-dashboard-tables/interfaces';
import CustomSortBy from '../../search/widgets/sorts/CustomSortBy';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import PurebredHybridSearchCTA from '../../search/widgets/PurebredHybridSearchCTA';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useScrollToRef from '../../../utils/hooks/useScrollToRef';
import useApplicableFilters from '../../../hooks/useApplicableFilters';
import CustomCurrentRefinements from '../../search/widgets/CustomCurrentRefinements/CustomCurrentRefinements';
import getInstantSelectDropdown from '../../search/widgets/filters/InstantSelectDropdown';
import { parsePriceAndAgeFromNumericFilters } from '../../search/utils';
import CustomCurrentAgeRefinement from '../../search/widgets/CustomCurrentRefinements/CustomCurrentAgeRefinement';
import CustomCurrentBreedRefinement from '../../search/widgets/CustomCurrentRefinements/CustomCurrentBreedRefinement';
import { ChevronDown } from '../../search/widgets/filters/react-select-custom-child-components/DropdownHelpers';
import PurebredHybridConnectedToggleContainer
  from '../../search/widgets/filters/PurebredHybridConnectedToggleContainer';
import RowViewCustomRenderListingInfiniteHits from '../../search/widgets/RowViewCustomRenderListingInfiniteHits';
import KeywordSearch from '../../search/widgets/KeywordSearch';

const DefaultSellerFilter = connectRefinementList(getEmptyFilter());
const DefaultListingsTypeFilter = connectRefinementList(getEmptyFilter());
const EmptyBreedFilter = connectRefinementList(getEmptyFilter());
const EmptyFilter = connectRefinementList(getEmptyFilter());
const DefaultAvailabilityFilter = connectRefinementList(getEmptyFilter());
const DefaultPublicationStatusFilter = connectRefinementList(getEmptyFilter());
const DefaultCityFilter = connectRefinementList(getEmptyFilter());
const CustomBreedFilter = connectRefinementList(getSelectDropdown(true, 'Breed', [], '', true, true));
const CustomInstantBreedFilter = connectRefinementList(getInstantSelectDropdown(true, 'Breed', [], '', true, true));
const CustomSexFilter = connectRefinementList(getSelectDropdown(true, 'Sex', [], '', false, true));
const CustomStateFilter = connectRefinementList(getSelectDropdown(true, 'State', [], '', false, true));
const CustomRegistrationFilter = connectRefinementList(getSelectDropdown(true, 'Registration', [], '', false, true));
const CustomGenerationFilter = connectRefinementList(getSelectDropdown(true, 'Generation', [], '', false, true));

interface SearchResultsBlockProps extends RouteComponentProps {
  breedSlug?: string;
  sellerSlug?: string;
  categorySlug?: any;
  filterByState?: string;
  filterByCity?: string;
  cityCoordinates?: {
    lat: string,
    lng: string,
  } | null,
  listingsType: string,
  currentSort: string,
  setCurrentSort: (sort: string) => void,
  showNonPublished?: boolean,
  onlyOnSale: string,
  puppiesUnderPrice?: string,
  availableToday: string,
  newArrivals: string,
  searchState: SearchState,
  setSearchState: React.Dispatch<React.SetStateAction<SearchState>>,
  instantRefinements?: string[],
  shouldSortByLocationOnLoad?: boolean,
}

// Since expires_at only updates on page load, there is a chance that expired
// listings could be returned if a page is open for a long time.
// The trade-off, however, is that we perform fewer queries,
// since InstantSearch can cache the initial query.
const defaultNumericFilters = [
  [
    `expires_at > ${moment().utc().format('X')}`,
    'can_expire = 0',
  ],
  [
    'isVisibleOnPublicSearch = 1',
  ],
];

const availableTodayForAlgolia = moment()
  .utc()
  .endOf('day')
  .format('X');

const newArrivalsForAlgolia = moment()
  .utc()
  .subtract(7, 'days')
  .format('X');

// Given the state refinement, turn it into an array of states currently being searched. For some
// reason, the refinement isn't always an array, so we need to wrap it in that case
const getStateArray = (states?: string | string[]): string[] => {
  if (!states) {
    return [];
  }

  if (isArray(states)) {
    return states;
  }

  return [states];
};

const getCityArray = (cities?: string | string[]): string[] => {
  if (!cities) {
    return [];
  }

  if (isArray(cities)) {
    return cities;
  }

  return [cities];
};

const getInitialRefinementsForSearch = (
  searchState: SearchState,
  refinementsToSkip: string[],
) => {
  const { refinementList: newRefinementList = {} } = searchState;

  const initialRefinementsForSearch = {};
  listingSearchRefinements
    .forEach((attribute) => {
      if (!refinementsToSkip.includes(attribute)) {
        // Don't add instantRefinements && ignoredRefinements
        initialRefinementsForSearch[attribute] = [];
      }
    });
  Object.keys(newRefinementList).forEach((attribute) => {
    if (!refinementsToSkip.includes(attribute)) {
      // Don't add instantRefinements && ignoredRefinements
      initialRefinementsForSearch[attribute] = newRefinementList[attribute];
    }
  });

  return initialRefinementsForSearch;
};

const SearchResultsBlock: React.FC<SearchResultsBlockProps> = (props): ReactElement => {
  const {
    breedSlug,
    sellerSlug,
    filterByState,
    filterByCity,
    cityCoordinates,
    listingsType,
    showNonPublished,
    searchState,
    setSearchState,
    currentSort,
    setCurrentSort,
    categorySlug,
    onlyOnSale,
    availableToday,
    newArrivals,
    shouldSortByLocationOnLoad,
    instantRefinements = [],
    puppiesUnderPrice = '',
  } = props;

  const {
    searchLocation,
    useSearchByLocation,
    toggleSearchByLocation,
  } = useRequestLocation(undefined, shouldSortByLocationOnLoad);

  const numericFiltersByRoute = useMemo(() => {
    const filters: string[] = [];

    if (puppiesUnderPrice !== '') {
      filters.push(`real_price <= ${puppiesUnderPrice}00`);
    }

    if (onlyOnSale === 'true') {
      filters.push('sale_price > 0');
    }

    if (availableToday === 'true') {
      filters.push(`date_available <= ${availableTodayForAlgolia}`);
    }

    if (newArrivals === 'true') {
      filters.push(`created_at > ${newArrivalsForAlgolia}`);
    }

    return filters;
  }, [
    puppiesUnderPrice,
    onlyOnSale,
    availableToday,
    newArrivals,
  ]);

  const fetchInitialMinPrice = () => {
    const { minPrice }: PriceParams = fetchSearchParams();
    if (minPrice && Number(minPrice)) {
      return currency(minPrice, { fromCents: true }).value.toString();
    }
    return undefined;
  };

  const fetchInitialMaxPrice = () => {
    const { maxPrice }: PriceParams = fetchSearchParams();
    if (maxPrice && Number(maxPrice)) {
      return currency(maxPrice, { fromCents: true }).value.toString();
    }
    return undefined;
  };

  const fetchInitialSelectedAgeValue = () => {
    const { age }: AgeParams = fetchSearchParams();
    const selectedAge = validAgeSelectionOptions.find((option) => option.value === age);
    if (selectedAge) {
      return [selectedAge];
    }
    return [];
  };

  const ignoredRefinements = [
    ...sellerSlug ? ['seller_id'] : [],
    ...breedSlug ? ['admin_breed_parents'] : [],
    ...categorySlug ? ['category'] : [],
    ...filterByState ? ['state'] : [],
    ...filterByCity ? ['city'] : [],
    ...listingsType ? ['listings_type'] : [],
    ...showNonPublished ? [] : ['status'],
    ...puppiesUnderPrice ? ['real_price'] : [],
    'status',
    'availability',
    'admin_single_breed',
  ];

  const fetchInitialAdminSingleBreedToggleState = (breedProvided: boolean) => {
    const { admin_single_breed }: QueryParams = fetchSearchParams();
    // If this is a breed page and an initial breed slug has been provided,
    // but `admin_single_breed` is not specified in the url query params,
    // we default to purebreds only on the initial search
    if (breedProvided && !admin_single_breed) {
      return {
        searchPurebredsOn: true,
        searchHybridsOn: false,
        hasBeenTouched: false,
      };
    }
    switch (admin_single_breed) {
      case 'true':
        return {
          searchPurebredsOn: true,
          searchHybridsOn: false,
          hasBeenTouched: false,
        };
      case 'false':
        return {
          searchPurebredsOn: false,
          searchHybridsOn: true,
          // If breed is provided, we want the "Hybrid" part of title to show immediately for SEO
          hasBeenTouched: breedProvided,
        };
      default:
        return {
          searchPurebredsOn: true,
          searchHybridsOn: true,
          hasBeenTouched: false,
        };
    }
  };

  const [minPrice, setMinPrice] = useState<string | undefined>(() => fetchInitialMinPrice());
  const [maxPrice, setMaxPrice] = useState<string | undefined>(() => fetchInitialMaxPrice());
  const [selectedAgeValue, setSelectedAgeValue] = useState<FacetObject[]>(() => {
    return fetchInitialSelectedAgeValue();
  });
  const [defaultSortRefinement] = useState<string>(ALGOLIA_ALL_LISTINGS_INDEX!);
  const customSortByOptions = useMemo(() => {
    const options = [
      {
        label: 'Relevance',
        value: defaultSortRefinement,
      },
    ];

    if (searchLocation) {
      options.push({
        label: 'Distance',
        value: `${ALGOLIA_ALL_LISTINGS_INDEX}_location`,
      });
    }

    return [
      ...options,
      {
        label: 'Most Recently Listed',
        value: `${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`,
      },
      {
        label: 'Lowest Price',
        value: `${ALGOLIA_ALL_LISTINGS_INDEX}_real_price_asc`,
      },
      {
        label: 'Highest Price',
        value: `${ALGOLIA_ALL_LISTINGS_INDEX}_real_price_desc`,
      },
    ];
  }, [defaultSortRefinement, searchLocation]);

  const [adminSingleBreedToggleState, setAdminSingleBreedToggleState] = useState(() => {
    return fetchInitialAdminSingleBreedToggleState(!!breedSlug);
  });
  const [query, setQuery] = useState(searchState?.query || '');

  const initialRefinementsRef: React.MutableRefObject<
    null | { [key: string]: string[] }
  > = useRef(null);
  // This pattern prevents `getInitialRefinementsForSearch` from running on every render;
  // cf. https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  if (initialRefinementsRef.current === null) {
    initialRefinementsRef.current = getInitialRefinementsForSearch(
      searchState,
      [
        ...instantRefinements,
        ...ignoredRefinements,
      ],
    );
  }

  const {
    isDirty,
    filtersListToApply,
    setNumericFilters,
    updatePendingRefinements,
    applyFilters,
    resetFilters,
    removeSelectedAttributeValue,
    addSelectedAttributeValue,
    clearQueuedOptionsFromPendingFilters,
  } = useApplicableFilters(
    initialRefinementsRef.current,
    [
      ...defaultNumericFilters,
      ...(searchState?.configure?.numericFilters || []),
    ],
  );

  const updateAdminSingleBreedToggleState = (
    toggledFieldState: AdminSingleBreedToggleStateInterface,
  ) => {
    setAdminSingleBreedToggleState((currentToggleState) => {
      const proposedToggleState = {
        ...currentToggleState,
        ...toggledFieldState,
        hasBeenTouched: true,
      };

      if (!proposedToggleState.searchPurebredsOn && !proposedToggleState.searchHybridsOn) {
        Swal.fire({
          html: '<p>You cannot disable both the <b>Purebred / Designer</b> and the <b>Hybrid / Mixed Breed</b> filters.</p>',
        });
        return currentToggleState;
      }
      return proposedToggleState;
    });
  };

  const breedSearchType: 'purebred' | 'hybrid' | '' = useMemo(() => {
    const {
      searchPurebredsOn,
      searchHybridsOn,
      hasBeenTouched,
    } = adminSingleBreedToggleState;

    if (!hasBeenTouched || (searchPurebredsOn && searchHybridsOn)) {
      return '';
    }
    if (searchPurebredsOn) {
      return 'purebred';
    }
    return 'hybrid';
  }, [adminSingleBreedToggleState]);

  const updateAgeQuery = useCallback((range: { min: string | number, max: string | number }) => {
    const {
      min,
      max,
    } = range;
    if (!min && !max) {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('birth_date');
        }),
      ]));
    } else {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('birth_date');
        }),
        `birth_date:${min} TO ${max}`,
      ]));
    }
  }, [setNumericFilters]);

  useEffect(() => {
    if (selectedAgeValue.length > 0) {
      const { value } = selectedAgeValue[0];
      if (value) {
        let newMin = '';
        let newMax = moment().utc().format('X');
        const numVal = parseInt(value, 10);
        if (numVal > 1) {
          newMin = moment().utc().subtract(numVal, 'months').format('X');
        } else {
          newMax = moment().utc().subtract(numVal, 'years').format('X');
        }

        updateAgeQuery({
          min: newMin ? parseInt(newMin, 10) : 0,
          max: parseInt(newMax, 10),
        });
      }
    } else {
      updateAgeQuery({
        min: '',
        max: '',
      });
    }
  }, [selectedAgeValue]);

  const updatePriceQuery = useCallback((min?: string | number, max?: string | number) => {
    if (min && !max) {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price >= ${min}`,
      ]));
    } else if (max && !min) {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price <= ${max}`,
      ]));
    } else if (!min && !max) {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
      ]));
    } else {
      setNumericFilters((currentNumericFilters) => ([
        ...currentNumericFilters.filter((item) => {
          if (Array.isArray(item)) {
            return true;
          }
          return !item.includes('real_price');
        }),
        `real_price:${min} TO ${max}`,
      ]));
    }
  }, [setNumericFilters]);

  const debouncePriceUpdate = useRef(
    debounce((min, max) => updatePriceQuery(min, max), 500),
  ).current;

  useEffect(() => {
    return () => {
      debouncePriceUpdate.cancel();
    };
  }, [debouncePriceUpdate]);

  useEffect(() => {
    const newMin = minPrice ? currency(minPrice).intValue : '';
    const newMax = maxPrice ? currency(maxPrice).intValue : '';
    debouncePriceUpdate(newMin, newMax);
  }, [debouncePriceUpdate, minPrice, maxPrice]);

  useEffect(() => {
    // Set initial page sort, if not default
    if (newArrivals) {
      // Sort by most recently posted
      setCurrentSort(`${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`);
    }
  }, []);

  const breedRefinement = breedSlug ? { defaultRefinement: [breedSlug] } : {};
  const sellerRefinement = sellerSlug ? { defaultRefinement: [sellerSlug] } : {};
  const categoryRefinement = categorySlug ? { defaultRefinement: [categorySlug] } : {};
  const stateRefinement = filterByState && !cityCoordinates
    ? { defaultRefinement: [filterByState] }
    : {};
  const cityRefinement = filterByCity && !cityCoordinates
    ? { defaultRefinement: [filterByCity] }
    : {};
  const listingsTypeRefinement = listingsType ? { defaultRefinement: [listingsType] } : {};
  const publicationStatusRefinement = showNonPublished ? {} : { defaultRefinement: ['published'] };
  const availabilityRefinement = { defaultRefinement: ['available', 'pending'] };

  const filterRefinements = (item) => {
    return !ignoredRefinements.includes(item.attribute);
  };
  /**
   * Update searchState when `filtersListToApply` is updated
   */
  // ADS FUNCTIONALITY REMOVED AS FOR LANPHP-336
  // const [atfRendered, setAtfRendered] = useState(false);
  // const [btfRendered, setBtfRendered] = useState(false);
  //
  // useEffect(() => {
  //   const atfObserver = new MutationObserver((mutations) => {
  //     mutations.forEach((mutation) => {
  //       if (mutation.type === 'childList') {
  //         if (!document.getElementById(FirstAdID)) {
  //           setAtfRendered?.(false);
  //         }
  //       }
  //     });
  //   });
  //
  //   const btfObserver = new MutationObserver((mutations) => {
  //     mutations.forEach((mutation) => {
  //       if (mutation.type === 'childList') {
  //         if (!document.getElementById(SecondAdID)) {
  //           setBtfRendered?.(false);
  //         }
  //       }
  //     });
  //   });
  //
  //   atfObserver.observe(document.body, {
  //     childList: true,
  //     subtree: true,
  //   });
  //   btfObserver.observe(document.body, {
  //     childList: true,
  //     subtree: true,
  //   });
  //
  //   return () => {
  //     atfObserver.disconnect();
  //     btfObserver.disconnect();
  //   };
  // }, []);
  //
  // useEffect(() => {
  //   if ((atfRendered && btfRendered) || atfRendered) {
  //     document.dispatchEvent(new CustomEvent('RefreshAds'));
  //   }
  // }, [atfRendered, btfRendered, filtersListToApply, currentSort, adminSingleBreedToggleState]);

  useEffect(() => {
    const {
      refinementList: refinementListToApply,
    } = filtersListToApply;

    setSearchState((currentSearchState) => {
      const { refinementList: currentRefinementList = {} } = currentSearchState;
      const newRefinementList = { ...refinementListToApply };

      const refinementsToPersist = [
        ...ignoredRefinements,
        ...instantRefinements,
      ];
      refinementsToPersist.forEach((refinementToPersist) => {
        if (currentRefinementList[refinementToPersist]) {
          newRefinementList[refinementToPersist] = currentRefinementList[refinementToPersist];
        }
      });

      return {
        ...currentSearchState,
        page: 1, // Page must be updated manually
        refinementList: newRefinementList,
      };
    });
  }, [filtersListToApply]);

  const {
    numericFilters,
    refinementList: refinementListToApply,
  } = filtersListToApply;

  const memoizedNumericFilters = useMemo(() => [
    ...numericFiltersByRoute,
    ...numericFilters,
  ], [
    numericFilters,
    numericFiltersByRoute,
  ]);

  const resetSelectedAge = useCallback(() => {
    const newNumericFilters = numericFilters.filter((filter) => {
      if (Array.isArray(filter)) {
        return true;
      }
      return filter.indexOf('birth_date') === -1;
    });
    setNumericFilters(newNumericFilters);
    resetFilters({
      ...filtersListToApply,
      numericFilters: newNumericFilters,
    });
    setSelectedAgeValue([]);
  }, [
    numericFilters,
    resetFilters,
    setNumericFilters,
    setSelectedAgeValue,
  ]);

  /**
   * This method is used as a callback to reset queued filters.
   *
   * When changing any instantaneous filters or settings, such as updating
   * `admin_breed_parents`/`admin_single_breed`, typing a new query string,
   * or sorting by 'location', we need to wipe queued filters that haven't
   * yet been applied, since some currently queued filter options may
   * no longer be available.
   */
  const resetQueuedFiltersCallback = useCallback(() => {
    clearQueuedOptionsFromPendingFilters();
    // return price input and age displays to correct state
    const {
      minPrice: newMinPrice,
      maxPrice: newMaxPrice,
      diffInMonths,
    } = parsePriceAndAgeFromNumericFilters(numericFilters);

    const convertedMinPrice = newMinPrice
      ? currency(newMinPrice, { fromCents: true }).value.toString()
      : '';
    if (convertedMinPrice !== minPrice) {
      setMinPrice(convertedMinPrice);
    }
    const convertedMaxPrice = newMaxPrice
      ? currency(newMaxPrice, { fromCents: true }).value.toString()
      : '';
    if (convertedMaxPrice !== maxPrice) {
      setMaxPrice(convertedMaxPrice);
    }

    const foundSelectedAgeValue = validAgeSelectionOptions.find(
      (option) => option.value === diffInMonths.toString(),
    );
    const newSelectedAgeValue = foundSelectedAgeValue ? [foundSelectedAgeValue] : [];
    if (!isEqual(newSelectedAgeValue, selectedAgeValue)) {
      setSelectedAgeValue(newSelectedAgeValue);
    }
  }, [
    clearQueuedOptionsFromPendingFilters,
    maxPrice,
    minPrice,
    numericFilters,
    selectedAgeValue,
  ]);

  useEffect(() => {
    const updatedQueryString = searchState.query || '';
    if (updatedQueryString !== query) {
      setQuery(updatedQueryString);
      resetQueuedFiltersCallback();
    }
  }, [query, searchState, resetQueuedFiltersCallback]);

  useEffect(() => {
    // Rest queued filters whenever the breed toggle-state changes
    resetQueuedFiltersCallback();
  }, [adminSingleBreedToggleState]);

  const customSortByCallback = (value: string) => {
    if (value === `${ALGOLIA_ALL_LISTINGS_INDEX}_location`) {
      // Since sorting by location can alter available filter options,
      // we should reset queued filters here
      resetQueuedFiltersCallback();
      toggleSearchByLocation(true);
    } else {
      toggleSearchByLocation(false);
    }
    setCurrentSort(value);
  };

  // If user turns on location, sort by location immediately
  useEffect(() => {
    if (
      ALGOLIA_SORT_BY_LOCATION_IF_SET
      && useSearchByLocation
      && currentSort !== `${ALGOLIA_ALL_LISTINGS_INDEX}_location`
    ) {
      // Since sorting by location can alter available filter options,
      // we should reset queued filters here
      resetQueuedFiltersCallback();
      setCurrentSort(`${ALGOLIA_ALL_LISTINGS_INDEX}_location`);
    }
  }, [useSearchByLocation]);

  const resultsContainerRef = useRef<HTMLElement>(null);
  const searchControlsRef = useRef<HTMLElement>(null);

  // When on mobile, "scroll to top" should take you to the top of search results (since the
  // controls take up the entire page). On desktop, the scroll should include the controls
  const { width } = useWindowDimensions();
  const scrollRef = width > 768 ? searchControlsRef : resultsContainerRef;
  const scrollToComponentTop = useScrollToRef(scrollRef);
  const scrollToSearchControls = useScrollToRef(searchControlsRef);

  const onCTAToggleSearchType = () => {
    setAdminSingleBreedToggleState((currentAdminSingleBreedToggleState) => {
      const {
        searchPurebredsOn,
        searchHybridsOn,
      } = currentAdminSingleBreedToggleState;

      // While it should not be possible to call this method using the UI when both
      // searchPurebredsOn and searchHybridsOn are true, we need to make sure that
      // it is NEVER possible to set both properties to false.
      if (searchPurebredsOn && searchHybridsOn) {
        return currentAdminSingleBreedToggleState;
      }
      return {
        searchPurebredsOn: !searchPurebredsOn,
        searchHybridsOn: !searchHybridsOn,
        hasBeenTouched: true,
      };
    });
    resetQueuedFiltersCallback();
    scrollToComponentTop();
  };

  const collapseFiltersMenu = useCallback(() => {
    const jqueryCollapsableFilterMenu = $('#collapseFiltersMenu');
    if (
      jqueryCollapsableFilterMenu.length > 0
      && jqueryCollapsableFilterMenu.hasClass('show')
      && jqueryCollapsableFilterMenu.collapse
    ) {
      // By using the event listener, we can scroll after the menu has collapsed.
      // Because the collapse is an async transition, this allows the scroll method
      // to find element positions in the DOM with accuracy.
      jqueryCollapsableFilterMenu.on('hidden.bs.collapse', () => {
        scrollToSearchControls();
        // Remove the listener, because we don't want to scroll on toggle; only on reset.
        jqueryCollapsableFilterMenu.off('hidden.bs.collapse');
      });
      jqueryCollapsableFilterMenu.collapse('hide');
    }
  }, [scrollToSearchControls]);

  /**
   * Callback method to use whenever filters are reset.
   * NB: Since these values are all tracked manually, and are reset separately from the connected
   * facets, resetting them may cause an additional query to be consumed.
   * This issue is mitigated by InstantSearch caching of initial state query.
   * E.g. if `admin_breed_parents` and `min_price` are both set, two separate queries will be made
   * to return to initial state.
   */
  const onReset = useCallback(() => {
    setNumericFilters(defaultNumericFilters);

    setQuery('');

    setSearchState((currentSearchState) => ({
      ...currentSearchState,
      query: '',
    }));

    if (minPrice) {
      setMinPrice(undefined);
    }
    if (maxPrice) {
      setMaxPrice(undefined);
    }

    if (selectedAgeValue.length > 0) {
      setSelectedAgeValue([]);
    }

    const clearedRefinements = {
      numericFilters: defaultNumericFilters,
      refinementList: {},
    };
    listingSearchRefinements.filter((attribute) => {
      return !ignoredRefinements.includes(attribute) && !instantRefinements.includes(attribute);
    })
      .forEach((attribute) => {
        clearedRefinements.refinementList[attribute] = [];
      });
    resetFilters(clearedRefinements);

    collapseFiltersMenu();
  }, [
    collapseFiltersMenu,
    ignoredRefinements,
    listingSearchRefinements,
    maxPrice,
    minPrice,
    resetFilters,
    scrollToSearchControls,
    selectedAgeValue,
  ]);

  const onApplyFilters = useCallback(() => {
    applyFilters();
    collapseFiltersMenu();
  }, [
    applyFilters,
    collapseFiltersMenu,
  ]);

  // If there are states being searched, we need to append them to the `searchTitle`
  const stateArray = useMemo(() => {
    const { refinementList = {} } = searchState;
    return getStateArray(refinementList.state);
  }, [searchState]);
  // If there are cities being searched, we need to append them to the `searchTitle`
  const cityArray = useMemo(() => {
    const { refinementList = {} } = searchState;
    return getCityArray(refinementList.city).map((city) => decodeURIComponent(city).replace(/\+/g, ' '));
  }, [searchState]);

  /**
   * The 'state' refinement may be set in the parent component, in which case
   * we want that specific "setting" to over-ride the queued and applied
   * 'state' filter.
   */
  useEffect(() => {
    const { state: statesToApply = [] } = refinementListToApply;

    if (!isEqual(stateArray, statesToApply)) {
      resetQueuedFiltersCallback();
      addSelectedAttributeValue('state', stateArray);
    }
  }, [stateArray]);

  const searchTitleLocation = useMemo(() => {
    let location: string[] = [];
    if (filterByCity) {
      location.push(filterByCity);
    }
    location = location.concat(cityArray);
    if (filterByState) {
      location.push(filterByState);
    }
    location = location.concat(stateArray);
    return location;
  }, [cityArray, stateArray, filterByCity, filterByState]);

  const generateSearchTitleTypeByListing = () => {
    let title = '';
    if (listingsType === 'puppy') {
      title = breedSearchType || breedSlug ? 'puppies for sale' : 'Puppies for sale';
    } else {
      title = breedSearchType || breedSlug ? 'studs' : 'Studs';
    }
    return title;
  };

  const generateBreedSearchType = () => `${breedSearchType.charAt(0).toUpperCase()}${breedSearchType.slice(1)}`;

  const searchTitle = `${
    breedSlug ? `${breedSlug} ` : ''
  } ${breedSlug ? generateBreedSearchType().toLowerCase() : generateBreedSearchType()} ${
    generateSearchTitleTypeByListing()
  } ${searchTitleLocation.length > 0 ? ` in ${searchTitleLocation.join(', ')}` : ''}`;

  // The CTA component shows the opposite of what the main search shows, which is why
  // Purebred & Hybrid are flipped; it will not show if both `searchPurebredsOn` and
  // `searchHybridsOn` are `true`
  const purebredHybridCTAString = useMemo(() => {
    const {
      searchPurebredsOn,
      searchHybridsOn,
    } = adminSingleBreedToggleState;
    if (searchPurebredsOn && searchHybridsOn) {
      return '';
    }
    if (searchPurebredsOn) {
      return 'Hybrid';
    }
    return 'Purebred';
  }, [adminSingleBreedToggleState]);

  const ctaTitle = `View ${
    breedSlug ? `${breedSlug} ` : ''
  } ${purebredHybridCTAString} ${
    listingsType === 'puppy' ? 'Puppies for sale' : 'Studs'
  }`;

  const showPurebredHybridSearchCTA = !adminSingleBreedToggleState.searchPurebredsOn
    || !adminSingleBreedToggleState.searchHybridsOn;

  const TitleTag = useMemo(() => {
    if (sellerSlug) {
      return 'h3';
    }
    if (
      categorySlug
      || filterByState
      || onlyOnSale === 'true'
      || puppiesUnderPrice !== ''
      || availableToday === 'true'
      || newArrivals === 'true'
    ) {
      return 'h2';
    }
    return 'h1';
  }, [
    availableToday,
    categorySlug,
    filterByState,
    newArrivals,
    onlyOnSale,
    sellerSlug,
    puppiesUnderPrice,
  ]);

  const aroundLatLng = useMemo(() => {
    if (cityCoordinates?.lat && cityCoordinates?.lng) {
      return `${cityCoordinates?.lat}, ${cityCoordinates?.lng}`;
    }
    return searchLocation || '';
  }, [
    cityCoordinates,
    searchLocation,
  ]);

  const aroundRadius = useMemo(() => {
    // If city coordinates are set, then we want to limit our search to within 100mi of the city
    if (cityCoordinates) {
      return 160934; // ~100 miles in meters
    }
    return 'all';
  }, [cityCoordinates]);

  return (
    <>
      <section className="results-container" ref={searchControlsRef}>
        <div className="filters-and-results-container">
          <div
            className="container-filters"
          >
            <div className="title-container">
              <h2>Filter</h2>
              <div className="clear-filters" data-layout="desktop">
                <ClearRefinements
                  onReset={onReset}
                  className="clear-filters-button"
                  label="Clear"
                  transformItems={(items) => items.filter(filterRefinements)}
                  disableIfEmpty={
                    (!minPrice && !maxPrice && selectedAgeValue.length === 0 && query.length < 3)
                    || !!puppiesUnderPrice
                  }
                />
              </div>
            </div>
            <Panel className="py-4">
              <PurebredHybridConnectedToggleContainer
                attribute="admin_single_breed"
                adminSingleBreedToggleState={adminSingleBreedToggleState}
                updateAdminSingleBreedToggleState={updateAdminSingleBreedToggleState}
                defaultRefinement={!breedSearchType
                  ? ['']
                  : [(breedSearchType === 'purebred').toString()]}
              />
            </Panel>
            <div className="filter-button-container text-center d-lg-none mb-2">
              <button
                className="btn btn-lg btn-outline-blue-500 text-capitalize w-100 position-relative collapsed"
                type="button"
                data-toggle="collapse"
                data-target="#collapseFiltersMenu"
                aria-expanded="false"
                aria-controls="collapseFiltersMenu"
              >
                <span>Filter Results</span>
                <span className="chevron-wrapper position-absolute">
                  <ChevronDown />
                </span>
              </button>
            </div>
            <div
              id="collapseFiltersMenu"
              className="collapse d-lg-flex flex-wrap flex-column align-content-stretch"
            >
              <div data-fsid="keyword-filter">
                <Panel>
                  <KeywordSearch placeholder="Search (min. 3 characters)" />
                </Panel>
              </div>

              {sellerSlug && (
              <DefaultSellerFilter
                attribute="seller_id"
                {...sellerRefinement}
              />
              )}
              {listingsType && (
              <DefaultListingsTypeFilter
                attribute="listings_type"
                {...listingsTypeRefinement}
              />
              )}
              <DefaultAvailabilityFilter
                attribute="availability"
                {...availabilityRefinement}
              />
              <DefaultCityFilter
                attribute="city"
                {...cityRefinement}
              />
              {!showNonPublished && (
              <DefaultPublicationStatusFilter
                attribute="status"
                {...publicationStatusRefinement}
              />
              )}
              {breedSlug
                ? <EmptyBreedFilter attribute="admin_breed_parents" {...breedRefinement} />
                : (
                  <div data-fsid="breed-filter">
                    <Panel>
                      {instantRefinements.includes('admin_breed_parents') ? (
                        <CustomInstantBreedFilter
                          limit={1000}
                          attribute="admin_breed_parents"
                          onRefineCallback={resetQueuedFiltersCallback}
                          {...breedRefinement}
                        />
                      ) : (
                        <CustomBreedFilter
                          limit={1000}
                          attribute="admin_breed_parents"
                          updatePendingRefinements={updatePendingRefinements}
                          applyFilters={onApplyFilters}
                          isDirty={isDirty}
                          {...breedRefinement}
                        />
                      )}
                    </Panel>
                  </div>
                )}
              <div data-fsid="sex-filter">
                <Panel>
                  <CustomSexFilter
                    attribute="sex"
                    updatePendingRefinements={updatePendingRefinements}
                    applyFilters={onApplyFilters}
                    isDirty={isDirty}
                  />
                </Panel>
              </div>
              <div data-fsid="age-filter">
                <Panel>
                  <CustomAgeFilter
                    options={validAgeSelectionOptions}
                    placeholder="Age"
                    label="Age"
                    selectedValue={selectedAgeValue}
                    setSelectedValue={setSelectedAgeValue}
                    applyFilters={onApplyFilters}
                    isDirty={isDirty}
                    isFullWidth
                  />
                </Panel>
              </div>
              <div data-fsid="state-filter">
                <Panel>
                  {filterByState ? (
                    <EmptyFilter
                      attribute="state"
                      {...stateRefinement}
                    />
                  ) : (
                    <CustomStateFilter
                      limit={100}
                      attribute="state"
                      updatePendingRefinements={updatePendingRefinements}
                      applyFilters={onApplyFilters}
                      isDirty={isDirty}
                    />
                  )}
                </Panel>
              </div>
              {categorySlug ? (
                <EmptyFilter
                  attribute="category"
                  {...categoryRefinement}
                />
              ) : null}
              <div data-fsid="registration-filter">
                <Panel>
                  <CustomRegistrationFilter
                    attribute="registrations"
                    updatePendingRefinements={updatePendingRefinements}
                    applyFilters={onApplyFilters}
                    isDirty={isDirty}
                  />
                </Panel>
              </div>
              <div data-fsid="generation-filter">
                <Panel>
                  <CustomGenerationFilter
                    attribute="generation"
                    updatePendingRefinements={updatePendingRefinements}
                    applyFilters={onApplyFilters}
                    isDirty={isDirty}
                  />
                </Panel>
              </div>
              <div data-fsid="price-filter">
                <Panel>
                  {puppiesUnderPrice ? (
                    <EmptyFilter attribute="real_price" />
                  ) : (
                    <CustomPriceFilter
                      applyFilters={onApplyFilters}
                      isDirty={isDirty}
                      min={minPrice || ''}
                      max={maxPrice || ''}
                      setMin={setMinPrice}
                      setMax={setMaxPrice}
                    />
                  )}
                </Panel>
              </div>
              <div className="ais-Panel d-flex justify-content-center align-content-center">
                <button
                  className="btn btn-primary rounded-lg font-weight-semibold mt-3 px-5 full-width"
                  onClick={onApplyFilters}
                  disabled={!isDirty}
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
            {/* Sort, ResultsPerPage, and CurrentRefinements */}
          </div>
          <div className="container-search">
            <TitleTag className="search-title text-center text-md-left font-weight-bold">{searchTitle}</TitleTag>
            <div className="d-flex flex-column flex-md-column-reverse w-100">
              {/* Sort and ResultsPerPage container */}
              <div
                className="d-flex flex-wrap flex-xl-nowrap align-self-xl-end justify-content-between w-100 pb-2 pb-xl-0 stats-and-sort-container"
              >
                <div className="mr-3 mb-1 text-nowrap">
                  <ResultsNumberMobile searchTitle={searchTitle} />
                </div>
                <div className="d-flex flex-wrap flex-xl-nowrap mb-1">
                  <SelectWidget
                    label="Sort By:"
                    className="mr-3 align-items-center"
                  >
                    <CustomSortBy
                      wrapperClass="container-option"
                      onChangeCallback={customSortByCallback}
                      defaultRefinement={defaultSortRefinement}
                      selected={currentSort}
                      items={customSortByOptions}
                    />
                  </SelectWidget>
                </div>
              </div>
              {/* CurrentRefinements container */}
              <div>
                <CustomCurrentRefinements
                  currentlyAppliedAttributes={Object.fromEntries(
                    Object.entries(refinementListToApply)
                      .filter(([key]) => !ignoredRefinements.includes(key)),
                  )}
                  onRemove={removeSelectedAttributeValue}
                >
                  <CustomCurrentAgeRefinement
                    numericFilters={numericFilters}
                    resetSelectedAge={resetSelectedAge}
                  />
                  <CustomCurrentBreedRefinement
                    attribute="admin_breed_parents"
                    instantRefinements={instantRefinements}
                    searchState={searchState}
                    setSearchState={setSearchState}
                    onRemoveCallback={resetQueuedFiltersCallback}
                  />
                </CustomCurrentRefinements>
              </div>
            </div>
            <Configure
              attributesToSnippet={['description:2']}
              snippetEllipsisText="…"
              removeWordsIfNoResults="allOptional"
              facetFilters={[`listings_type:${listingsType}`]}
              numericFilters={memoizedNumericFilters}
              query={query}
              aroundRadius={aroundRadius}
              aroundPrecision={useSearchByLocation ? undefined : algoliaPrecisionSettings}
              aroundLatLng={aroundLatLng}
              restrictSearchableAttributes={publiclySearchableAllListingsAttributes}
              attributesToRetrieve={listingAttributesToRetrievePuppyCard}
              attributesToHighlight={listingAttributesToHighlightPuppyCard}
              clickAnalytics
            />
            <main className="row-view-search-results-container" ref={resultsContainerRef}>
              <section className="row-view-container-results">
                <RowViewCustomRenderListingInfiniteHits
                  breedSlug={breedSlug}
                  hybrid={searchState.refinementList?.admin_single_breed
                      && searchState.refinementList?.admin_single_breed[0] === 'false'}
                  listingsType={listingsType}
                  setSearchState={setSearchState}
                />
                <NoResults />
              </section>
            </main>
          </div>
        </div>

      </section>
      {showPurebredHybridSearchCTA && (
        <PurebredHybridSearchCTA
          count={5}
          title={ctaTitle}
          listingsType={listingsType}
          searchState={searchState}
          selectedAgeValue={selectedAgeValue}
          searchIsHybrid={breedSearchType === 'hybrid'}
          toggleSearchType={onCTAToggleSearchType}
          externalBreedRefinement={breedSlug}
          externalSellerRefinement={sellerSlug
            ? parseInt(sellerSlug, 10)
            : undefined}
        />
      )}
    </>
  );
};

export default withRouter(SearchResultsBlock);
